import apiClient from "../../services/apiClient";
import { useEffect, useState, useContext, useMemo, useRef } from "react";
import { Input, Select, Button, Alert, Spin, Card, Tag, Pagination, Modal, Table, Descriptions, Form, Tooltip, Popconfirm, message } from "antd";
import { ThemeContext } from "../../context/ThemeContext";
import { EyeOutlined, TableOutlined, AppstoreOutlined, ArrowLeftOutlined, SearchOutlined, InfoCircleOutlined, DollarOutlined, FileTextOutlined, BankOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import "./NSDL.css";

const { Search } = Input;
const { Option } = Select;

const CertificatesOfDeposit = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [selectedIssuer, setSelectedIssuer] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [sortBy, setSortBy] = useState("");
  const [viewMode, setViewMode] = useState("table");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  });
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const editForm = useRef();
  const { isDark } = useContext(ThemeContext);
  const navigate = useNavigate();

  // Fetch paginated data from backend
  const fetchData = async (page = 1, pageSize = 20) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(
        `${baseUrl}/nsdl/certificates-of-deposit?page=${page - 1}&size=${pageSize}`,
        { validateStatus: () => true }
      );
      if (response.status === 204) {
        setData([]);
        setPagination({ current: 1, pageSize, total: 0, totalPages: 0 });
        return;
      }
      if (response.data && (response.data.content || response.data.data)) {
        const d = response.data.content ? response.data : response.data.data;
        setData(Array.isArray(d.content) ? d.content : []);
        setPagination({
          current: (d.pageNumber || 0) + 1,
          pageSize: d.pageSize || pageSize,
          total: d.totalElements || 0,
          totalPages: d.totalPages || 0,
        });
      } else {
        throw new Error(response.data.message || "Failed to fetch data");
      }
    } catch (error) {
      setError("Failed to load certificates of deposit data. Please try again.");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(pagination.current, pagination.pageSize);
    // eslint-disable-next-line
  }, []);

  // Filtering and sorting (client-side for current page)
  const filteredData = useMemo(() => {
    let filtered = Array.isArray(data) ? data : [];
    if (!Array.isArray(filtered)) return [];
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((item) =>
        (item.isin && item.isin.toLowerCase().includes(searchLower)) ||
        (item.companyName && item.companyName.toLowerCase().includes(searchLower)) ||
        (item.description && item.description.toLowerCase().includes(searchLower))
      );
    }
    if (selectedIssuer !== "all") {
      filtered = filtered.filter((item) => item.companyName === selectedIssuer);
    }
    if (selectedStatus !== "all") {
      filtered = filtered.filter((item) => item.creditRatingDetails === selectedStatus);
    }
    // No sort for now
    return filtered;
  }, [data, searchText, selectedIssuer, selectedStatus, sortBy]);

  // Unique values for filters (from current page)
  const { issuers, statuses } = useMemo(() => {
    const uniqueIssuers = [...new Set(data.map((item) => item.companyName))].filter(Boolean);
    const uniqueStatuses = [...new Set(data.map((item) => item.creditRatingDetails))].filter(Boolean);
    return { issuers: uniqueIssuers, statuses: uniqueStatuses };
  }, [data]);

  // Memoized stats for hero section
  const stats = useMemo(
    () => [
      {
        title: "Total Certificates",
        value: pagination.total.toLocaleString(),
        icon: <FileTextOutlined />,
        color: "#4ecdc4",
      },
      {
        title: "Active Issuers",
        value: issuers.length.toLocaleString(),
        icon: <BankOutlined />,
        color: "#667eea",
      },
      {
        title: "Total Volume",
        value: "₹--", // Placeholder, update if you have volume data
        icon: <DollarOutlined />,
        color: "#ff6b6b",
      },
    ],
    [pagination.total, issuers.length]
  );

  // Custom stat background colors
  const statBgColors = ["#4ecdc4", "#667eea", "#ff6b6b"];

  // Format value for display
  const formatValue = (value, key) => {
    if (value === null || value === undefined || value === "") return "N/A";
    if (key && key.toLowerCase().includes("date")) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) return date.toLocaleDateString();
    }
    if (typeof value === "string" && value.includes("\n")) {
      return <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{value}</pre>;
    }
    return value.toString();
  };

  // Pagination change handler
  const handlePageChange = (page, pageSize) => {
    fetchData(page, pageSize);
  };

  // Placeholder for details modal
  const showRecordDetails = (record) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditData(record);
    setEditModalVisible(true);
  };
  const handleUpdate = async (values) => {
    setEditLoading(true);
    try {
      await apiClient.put(`${baseUrl}/nsdl/certificates-of-deposit/${editData.isin}`, values);
      message.success('Record updated successfully');
      setEditModalVisible(false);
      fetchData(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error('Failed to update record');
    } finally {
      setEditLoading(false);
    }
  };
  const handleDelete = async (isin) => {
    try {
      await apiClient.delete(`${baseUrl}/nsdl/certificates-of-deposit/${isin}`);
      message.success('Record deleted successfully');
      fetchData(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error('Failed to delete record');
    }
  };

  return (
    <div className={`nsdl-container ${isDark ? "dark" : ""}`}>
      <Button
        type="default"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/d/nsdl")}
        style={{ marginBottom: 16 }}
      >
        Back to NSDL
      </Button>
      {/* Hero Section */}
      <div
        className="nsdl-hero"
        style={{
          background: 'linear-gradient(90deg, #3AA015 0%, #F49A24 50%, rgba(255, 0, 4, 0.61) 100%)',
          borderRadius: 18,
          padding: '2px',
          marginBottom: 32,
          position: 'relative',
        }}
      >
        <div
          style={{
            background: 'transparent',
            borderRadius: 16,
            padding: "48px 48px 40px 48px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            position: 'relative',
            overflow: 'hidden',
            minHeight: 180,
            width: '100%',
            height: '100%',
          }}
        >
          {/* Overlay for better text readability */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isDark 
                ? 'linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.5) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
              borderRadius: 16,
            }}
          />

          {/* Content */}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1
                style={{
                  margin: 0,
                  marginBottom: 12,
                  fontWeight: 800,
                  fontSize: 42,
                  color: isDark ? "#ffffff" : "#1a1a1a",
                }}
              >
                Certificates of Deposit
              </h1>
              <p
                style={{
                  fontSize: 20,
                  color: isDark ? "rgba(255, 255, 255, 0.9)" : "#4a4a4a",
                  fontWeight: 400,
                  lineHeight: 1.4,
                  margin: 0,
                }}
              >
                Short-term negotiable instruments issued by banks and financial institutions with detailed issuer information and rates
              </p>
            </div>

            {/* Stats on the right side - 2 per row */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-end' }}>
              {/* First row - 2 stats */}
              <div style={{ display: 'flex', gap: '12px' }}>
                {stats.slice(0, 2).map((stat, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                    padding: '12px 16px',
                    borderRadius: 12,
                    boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    minWidth: '180px'
                  }}>
                    <div
                      style={{
                        color: stat.color,
                        fontSize: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.9)',
                        borderRadius: 8,
                        boxShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      {stat.icon}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: isDark ? "#ffffff" : "#1a1a1a",
                        lineHeight: 1
                      }}>
                        {stat.value}
                      </span>
                      <span style={{
                        fontSize: 12,
                        color: isDark ? "rgba(255, 255, 255, 0.8)" : "#666",
                        fontWeight: 500
                      }}>
                        {stat.title}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Second row - remaining stats */}
              {stats.length > 2 && (
                <div style={{ display: 'flex', gap: '12px' }}>
                  {stats.slice(2).map((stat, index) => (
                    <div key={index + 2} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                      padding: '12px 16px',
                      borderRadius: 12,
                      boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                      minWidth: '180px'
                    }}>
                      <div
                        style={{
                          color: stat.color,
                          fontSize: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 36,
                          height: 36,
                          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.9)',
                          borderRadius: 8,
                          boxShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        {stat.icon}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <span style={{
                          fontSize: 20,
                          fontWeight: 700,
                          color: isDark ? "#ffffff" : "#1a1a1a",
                          lineHeight: 1
                        }}>
                          {stat.value}
                        </span>
                        <span style={{
                          fontSize: 12,
                          color: isDark ? "rgba(255, 255, 255, 0.8)" : "#666",
                          fontWeight: 500
                        }}>
                          {stat.title}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="nsdl-section">

        {/* Controls */}
        <div className="nsdl-controls">
          <div className="nsdl-controls-left">
            <Search
              placeholder="Search by ..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
            />
            <Select
              value={selectedIssuer}
              onChange={setSelectedIssuer}
              placeholder="Filter by Issuer"
              style={{ width: 200 }}
              allowClear
            >
              <Option value="all">All Issuers</Option>
              {issuers.map((issuer) => (
                <Option key={issuer} value={issuer}>
                  {issuer}
                </Option>
              ))}
            </Select>
            <Select
              value={selectedStatus}
              onChange={setSelectedStatus}
              placeholder="Filter by Status"
              style={{ width: 150 }}
              allowClear
            >
              <Option value="all">All Status</Option>
              {statuses.map((status) => (
                <Option key={status} value={status}>
                  {status}
                </Option>
              ))}
            </Select>
          </div>
          <div className="nsdl-controls-right">
            <Button.Group>
              <Button
                type={viewMode === "cards" ? "primary" : "default"}
                icon={<AppstoreOutlined />}
                onClick={() => setViewMode("cards")}
              >
                Cards
              </Button>
              <Button
                type={viewMode === "table" ? "primary" : "default"}
                icon={<TableOutlined />}
                onClick={() => setViewMode("table")}
              >
                Table
              </Button>
            </Button.Group>
          </div>
        </div>
        {/* Results Info */}
        <div className="nsdl-results-info">
          <span>
            Showing {filteredData.length} of {pagination.total} certificates (Page {pagination.current} of {pagination.totalPages})
          </span>
        </div>
        {/* Error Alert */}
        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: "1rem" }}
          />
        )}
        {/* Data Display */}
        <div className="nsdl-data-container">
          {loading ? (
            <div className="nsdl-loading">
              <Spin size="large" />
              <p>Loading certificates of deposit data...</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="nsdl-empty">
              <InfoCircleOutlined />
              <p>No certificates of deposit found matching your criteria</p>
            </div>
          ) : viewMode === "cards" ? (
            <div className="nsdl-cards-grid">
              {filteredData.map((record, index) => (
                <Card key={index} className="nsdl-data-card" hoverable>
                  <div className="nsdl-card-header">
                    <h3 className="nsdl-card-title">
                      <span className="nsdl-isin-code">{record.isin || `ISIN ${index + 1}`}</span>
                    </h3>
                  </div>
                  <div className="nsdl-card-content">
                    <div className="nsdl-card-field">
                      <span className="nsdl-field-label">Company:</span>
                      <span className="nsdl-field-value nsdl-company-name">{record.companyName || "N/A"}</span>
                    </div>
                    <div className="nsdl-card-field">
                      <span className="nsdl-field-label">Description:</span>
                      <span className="nsdl-field-value">{record.description || "N/A"}</span>
                    </div>
                    <div className="nsdl-card-field">
                      <span className="nsdl-field-label">Features:</span>
                      <span className="nsdl-field-value">{formatValue(record.featuresOfTheInstrument, "featuresOfTheInstrument")}</span>
                    </div>
                    <div className="nsdl-card-field">
                      <span className="nsdl-field-label">Redemption Date:</span>
                      <span className="nsdl-field-value">{formatValue(record.dateOfRedemption, "dateOfRedemption")}</span>
                    </div>
                  </div>
                  <div className="card-actions" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
                    <Tooltip title="View">
                      <Button
                        type="primary"
                        icon={<EyeOutlined />}
                        onClick={() => showRecordDetails(record)}
                      />
                    </Tooltip>
                    <Tooltip title="Edit">
                      <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                      />
                    </Tooltip>
                    <Tooltip title="Delete">
                      <Popconfirm
                        title="Are you sure you want to delete this record?"
                        onConfirm={() => handleDelete(record.isin)}
                        onCancel={() => message.error('Delete cancelled')}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Tooltip>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Table
              dataSource={filteredData}
              columns={[
                {
                  title: "ISIN",
                  dataIndex: "isin",
                  key: "isin",
                  render: (text) => <span className="isin-code">{text || "N/A"}</span>,
                },
                {
                  title: "Company",
                  dataIndex: "companyName",
                  key: "companyName",
                  render: (text) => <span>{text || "N/A"}</span>,
                },
                {
                  title: "Description",
                  dataIndex: "description",
                  key: "description",
                  render: (text) => <span>{text || "N/A"}</span>,
                },
                {
                  title: "Features",
                  dataIndex: "featuresOfTheInstrument",
                  key: "featuresOfTheInstrument",
                  render: (text) => formatValue(text, "featuresOfTheInstrument"),
                },
                {
                  title: "Redemption Date",
                  dataIndex: "dateOfRedemption",
                  key: "dateOfRedemption",
                  render: (text) => formatValue(text, "dateOfRedemption"),
                },
                {
                  title: "Actions",
                  key: "actions",
                  width: 150,
                  render: (_, record) => (
                    <div>
                      <Tooltip title="View">
                        <Button type="link" icon={<EyeOutlined />} onClick={() => showRecordDetails(record)} />
                      </Tooltip>
                      <Tooltip title="Edit">
                        <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                      </Tooltip>
                      <Popconfirm
                        title="Are you sure you want to delete this record?"
                        onConfirm={() => handleDelete(record.isin)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Tooltip title="Delete">
                          <Button type="link" icon={<DeleteOutlined />} danger />
                        </Tooltip>
                      </Popconfirm>
                    </div>
                  ),
                },
              ]}
              rowKey={(record, index) => index}
              pagination={false}
              className={`nsdl-table ${isDark ? "dark" : ""}`}
            />
          )}
        </div>
        {/* Pagination */}
        <div style={{ textAlign: "center", marginTop: "2rem" }}>
          <Pagination
            current={pagination.current}
            total={pagination.total}
            pageSize={pagination.pageSize}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} items`
            }
            onChange={handlePageChange}
            className="custom-pagination"
          />
        </div>
        {/* Details Modal */}
        <Modal
          title={selectedRecord ? `Certificate Details - ${selectedRecord.isin || ""}` : ""}
          open={detailModalVisible}
          onCancel={() => setDetailModalVisible(false)}
          footer={null}
          width={800}
          destroyOnClose
        >
          {selectedRecord ? (
            <Descriptions bordered column={1} size="small">
              {Object.entries(selectedRecord).map(([key, value]) => (
                <Descriptions.Item label={key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())} key={key}>
                  {formatValue(value, key)}
                </Descriptions.Item>
              ))}
            </Descriptions>
          ) : null}
        </Modal>
        {editModalVisible && editData && (
          <Modal
            title="Edit Record"
            open={editModalVisible}
            onCancel={() => setEditModalVisible(false)}
            footer={null}
          >
            <Form
              initialValues={editData}
              onFinish={handleUpdate}
              layout="vertical"
              ref={editForm}
            >
              <Form.Item noStyle>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                  {(() => {
                    const fields = Object.entries(editData).filter(([key]) => key !== 'createdAt' && key !== 'updatedAt');
                    const items = [];
                    for (let i = 0; i < fields.length; i += 2) {
                      const [key1] = fields[i];
                      const [key2] = fields[i + 1] || [];
                      items.push(
                        <div key={key1 + (key2 || '')} style={{ display: 'flex', width: '100%', gap: 16 }}>
                          <Form.Item
                            key={key1}
                            label={key1.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            name={key1}
                            rules={key1 === "isin" ? [] : [{ required: false }]}
                            style={{ flex: 1 }}
                          >
                            {key1 === "isin" ? <Input disabled /> : <Input />}
                          </Form.Item>
                          {key2 && (
                            <Form.Item
                              key={key2}
                              label={key2.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                              name={key2}
                              rules={key2 === "isin" ? [] : [{ required: false }]}
                              style={{ flex: 1 }}
                            >
                              {key2 === "isin" ? <Input disabled /> : <Input />}
                            </Form.Item>
                          )}
                        </div>
                      );
                    }
                    return items;
                  })()}
                </div>
              </Form.Item>
              <div style={{ textAlign: "right" }}>
                <Button onClick={() => setEditModalVisible(false)} style={{ marginRight: 8 }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={editLoading}>
                  Update
                </Button>
              </div>
            </Form>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default CertificatesOfDeposit; 